import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { ApiError } from '@/lib/api';
import { open_file_in_new_tab } from '@/components/auth-image';
import { use_agreement, use_draft, use_save_terms } from '@/features/onboarding/hooks';
import { TERMS_VERSION } from '@/features/onboarding/types';

export function TermsPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const save_m = use_save_terms();
  const agreement_q = use_agreement();
  const [accept, set_accept] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [opening_id, set_opening_id] = useState<string | null>(null);
  const module_agreements = agreement_q.data?.agreements ?? [];
  const show_legacy_card =
    module_agreements.length === 0 &&
    Boolean(agreement_q.data?.available && agreement_q.data.download_url);

  async function open_agreement(id: string, path: string) {
    set_opening_id(id);
    try {
      await open_file_in_new_tab(path);
    } catch {
      set_error('Could not open the agreement. Please try again.');
    } finally {
      set_opening_id(null);
    }
  }

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    if (!accept) {
      set_error('You must accept the terms to continue');
      return;
    }
    try {
      await save_m.mutateAsync({ terms_version: TERMS_VERSION, accept: true });
      navigate('/onboarding/submit');
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Could not save terms acceptance');
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="terms"
        title="Accept the terms"
        description={`Version ${TERMS_VERSION}. Vendor agreement covering platform fees, payouts, and dispute resolution.`}
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="terms-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Accept & continue
          </Button>
        }
      >
        <div className="max-h-72 overflow-y-auto rounded-md border border-border bg-muted/30 p-4 text-sm leading-6 text-muted-foreground">
          <p>
            By accepting these terms you confirm the business details supplied are accurate, agree
            to the platform commission schedule attached to your module category, and authorise the
            platform to collect and process customer information necessary for fulfilling bookings.
          </p>
          <p className="mt-3">
            Pet Marketplace reserves the right to suspend or reject any vendor whose submitted
            documents fail KYC verification. Full terms of service are available on request from
            <span className="font-semibold"> legal@petbuddy.in</span>.
          </p>
        </div>
        {/*
          Per-module agreements: the current version for each of the vendor's modules. Accepting
          this step accepts every listed version server-side.
        */}
        {module_agreements.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="module-agreements">
            {module_agreements.map((a) => (
              <div
                key={a.id}
                className="flex flex-col gap-3 rounded-md border-2 border-primary p-4 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`module-agreement-${a.module_type}`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="text-sm">
                    <p className="font-semibold">
                      {a.module_label} agreement · v{a.version}
                    </p>
                    {a.change_summary && (
                      <p className="text-muted-foreground">{a.change_summary}</p>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={opening_id === a.id}
                  onClick={() => open_agreement(a.id, a.file_path)}
                  data-testid={`module-agreement-download-${a.module_type}`}
                >
                  {opening_id === a.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download
                </Button>
              </div>
            ))}
          </div>
        )}
        {/*
          Fallback (9 Oct 2026 client change): the older single agreement PDF to download, sign and
          email back — shown only when no per-module agreements have been published.
        */}
        {show_legacy_card && agreement_q.data?.download_url && (
          <div
            className="flex flex-col gap-3 rounded-md border-2 border-primary p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="agreement-card"
          >
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm">
                <p className="font-semibold">Vendor agreement</p>
                <p className="text-muted-foreground">
                  Download the agreement, fill in your details and sign it
                  {agreement_q.data.return_email ? (
                    <>
                      , then email it to{' '}
                      <a
                        href={`mailto:${agreement_q.data.return_email}?subject=${encodeURIComponent('Signed vendor agreement')}`}
                        className="font-semibold text-primary underline"
                        data-testid="agreement-email"
                      >
                        {agreement_q.data.return_email}
                      </a>
                    </>
                  ) : null}
                  .
                </p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <a href={agreement_q.data.download_url} target="_blank" rel="noreferrer" download data-testid="agreement-download">
                <Download className="h-4 w-4" /> Download agreement
              </a>
            </Button>
          </div>
        )}
        <label className="flex items-start gap-2 text-sm" data-testid="terms-label">
          <input
            type="checkbox"
            data-testid="terms-checkbox"
            checked={accept}
            onChange={(e) => set_accept(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
          />
          <span>
            I have read and accept the vendor terms of service (v{TERMS_VERSION})
            {module_agreements.length > 0
              ? module_agreements.length === 1
                ? ' and the agreement listed above'
                : ' and the agreements listed above'
              : ''}
            .
          </span>
        </label>
      </StepSection>
    </form>
  );
}
